import apiClient from './api';
import {
  PostVariation,
  CreateVariationData,
  UpdateVariationData,
  DuplicateVariationData,
  UpdateVariationMediaData,
  ReorderMediaData,
  VariationTitleSuggestion,
} from '@/types/variations';
import { PlatformOptions, ScheduledInstance } from '@/types/scheduled-instances';
import { CalendarMapping } from '@/types/calendar-mappings';

/**
 * Post Variations Service
 *
 * API calls for post variation management
 */

// Re-export types for convenience
export type {
  PostVariation,
  CreateVariationData,
  UpdateVariationData,
  DuplicateVariationData,
  UpdateVariationMediaData,
  ReorderMediaData,
  VariationTitleSuggestion,
  PlatformOptions,
  ScheduledInstance,
  CalendarMapping,
};

export const variationsService = {
  /**
   * Get unique variation titles for autocomplete suggestions
   * Returns titles with usage count, sorted by most used
   */
  getTitles: async (): Promise<VariationTitleSuggestion[]> => {
    const response = await apiClient.get('/api/variations/titles');
    return response.data.data;
  },

  // Convenience aliases (shorter names)
  list: async (postId: number): Promise<PostVariation[]> => {
    const response = await apiClient.get(`/api/posts/${postId}/variations`);
    return response.data.data;
  },

  get: async (postId: number, variationId: number): Promise<PostVariation> => {
    const response = await apiClient.get(
      `/api/posts/${postId}/variations/${variationId}`
    );
    return response.data.data;
  },

  // Alias for get
  getById: async (postId: number, variationId: number): Promise<PostVariation> => {
    const response = await apiClient.get(
      `/api/posts/${postId}/variations/${variationId}`
    );
    return response.data.data;
  },

  /**
   * One variation in the exact shape the Variations table rows (and therefore the editor) use: read from
   * the team list endpoint filtered to its post, so caption / media / hashtags / schedule / release facts
   * are identical to opening it from the table. Falls back to the single-fetch shape only if the row is
   * not in the first page of that post's versions.
   */
  getForEditor: async (postId: number, variationId: number): Promise<PostVariation> => {
    const list = await variationsService.getTeamVariations({ postId, limit: 100 });
    const row = list.data.find((v) => v.id === variationId);
    return row ?? variationsService.getById(postId, variationId);
  },

  create: async (postId: number, data: CreateVariationData): Promise<PostVariation> => {
    const response = await apiClient.post(`/api/posts/${postId}/variations`, data);
    return response.data.data;
  },

  update: async (
    postId: number,
    variationId: number,
    data: UpdateVariationData
  ): Promise<PostVariation> => {
    const response = await apiClient.patch(
      `/api/posts/${postId}/variations/${variationId}`,
      data
    );
    return response.data.data;
  },

  delete: async (postId: number, variationId: number): Promise<void> => {
    await apiClient.delete(`/api/posts/${postId}/variations/${variationId}`);
  },

  duplicate: async (
    postId: number,
    variationId: number,
    newTitle: string
  ): Promise<PostVariation> => {
    const response = await apiClient.post(
      `/api/posts/${postId}/variations/${variationId}/duplicate`,
      { newTitle }
    );
    return response.data.data;
  },

  /**
   * Create a new variation for a post (alias for create)
   */
  createVariation: async (
    postId: number,
    data: CreateVariationData
  ): Promise<PostVariation> => {
    const response = await apiClient.post(`/api/posts/${postId}/variations`, data);
    return response.data.data;
  },

  /**
   * Get all variations for a post (alias for list)
   */
  getVariations: async (postId: number): Promise<PostVariation[]> => {
    const response = await apiClient.get(`/api/posts/${postId}/variations`);
    return response.data.data;
  },

  /**
   * Get a single variation by ID (alias for get)
   */
  getVariation: async (postId: number, variationId: number): Promise<PostVariation> => {
    const response = await apiClient.get(
      `/api/posts/${postId}/variations/${variationId}`
    );
    return response.data.data;
  },

  /**
   * Update an existing variation
   */
  updateVariation: async (
    postId: number,
    variationId: number,
    data: UpdateVariationData
  ): Promise<PostVariation> => {
    const response = await apiClient.patch(
      `/api/posts/${postId}/variations/${variationId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a variation
   */
  deleteVariation: async (postId: number, variationId: number): Promise<void> => {
    await apiClient.delete(`/api/posts/${postId}/variations/${variationId}`);
  },

  /**
   * Duplicate a variation with a new title
   */
  duplicateVariation: async (
    postId: number,
    variationId: number,
    data: DuplicateVariationData
  ): Promise<PostVariation> => {
    const response = await apiClient.post(
      `/api/posts/${postId}/variations/${variationId}/duplicate`,
      data
    );
    return response.data.data;
  },

  /**
   * Update variation media items (replace all media)
   */
  updateVariationMedia: async (
    postId: number,
    variationId: number,
    data: UpdateVariationMediaData
  ): Promise<PostVariation> => {
    const response = await apiClient.put(
      `/api/posts/${postId}/variations/${variationId}/media`,
      data
    );
    return response.data.data;
  },

  /**
   * Reorder variation media items
   */
  reorderVariationMedia: async (
    postId: number,
    variationId: number,
    data: ReorderMediaData
  ): Promise<PostVariation> => {
    const response = await apiClient.patch(
      `/api/posts/${postId}/variations/${variationId}/media/reorder`,
      data
    );
    return response.data.data;
  },

  /**
   * Get all variations for the team (across all posts)
   * Supports filtering by type, contentStatus, search, labels, and pagination
   */
  getTeamVariations: async (params?: {
    type?: 'language' | 'custom';
    contentStatus?: 'DRAFT' | 'READY';
    search?: string;
    labels?: string;
    language?: string;
    workflowStatus?: 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';
    postId?: number;
    page?: number;
    limit?: number;
  }): Promise<{
    data: PostVariation[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
      hasMore: boolean;
    };
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.contentStatus) queryParams.append('contentStatus', params.contentStatus);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.labels) queryParams.append('labels', params.labels);
    if (params?.language) queryParams.append('language', params.language);
    if (params?.workflowStatus) queryParams.append('workflowStatus', params.workflowStatus);
    if (params?.postId) queryParams.append('postId', String(params.postId));
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `/api/variations${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get(url);
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  /**
   * Map a variation to a calendar date
   * Uses the existing calendar mappings endpoint with variationId
   */
  mapToCalendar: async (
    postId: number,
    variationId: number,
    data: {
      calendarId: number;
      plannedDate: Date | string;
      calendarNoteId?: number;
      calendarHolidayId?: number;
    }
  ): Promise<CalendarMapping> => {
    const response = await apiClient.post<{ success: boolean; data: CalendarMapping }>(
      '/api/calendar-mappings',
      {
        postId,
        variationId,
        calendarId: data.calendarId,
        plannedDate: typeof data.plannedDate === 'string' ? data.plannedDate : data.plannedDate.toISOString(),
        calendarNoteId: data.calendarNoteId,
        calendarHolidayId: data.calendarHolidayId,
      }
    );
    return response.data.data;
  },

  /**
   * Shoot a mapped variation (create scheduled instance)
   * Uses the existing scheduled instances shoot endpoint
   */
  shoot: async (data: {
    calendarMappingId: number;
    scheduledAt: Date | string;
    channelIds: number[];
    channelOptions?: Record<number, PlatformOptions>;
    immediate?: boolean;
  }): Promise<ScheduledInstance> => {
    const response = await apiClient.post<{ success: boolean; data: ScheduledInstance }>(
      '/api/scheduled-instances/shoot',
      {
        ...data,
        scheduledAt: typeof data.scheduledAt === 'string' ? data.scheduledAt : data.scheduledAt.toISOString(),
      }
    );
    return response.data.data;
  },

  /**
   * Update variation content status (DRAFT → READY)
   */
  updateContentStatus: async (
    postId: number,
    variationId: number,
    contentStatus: 'DRAFT' | 'READY'
  ): Promise<PostVariation> => {
    const response = await apiClient.patch(
      `/api/posts/${postId}/variations/${variationId}`,
      { contentStatus }
    );
    return response.data.data;
  },
};
